import { AvailableCarriersRequest, CarrierPreviewItem, Order, ServiceTypeEnum } from '@/types';

/**
 * Builds AvailableCarriersRequest for the new POST /orders/available-carriers endpoint.
 *
 * Bug E (2026-05-17): when a shipping rule restricts the carrier set, the
 * caller forwards the restricted_carrier_uids here so the backend can ensure
 * every rule-mandated carrier surfaces in the response — even when
 * DispatchService would drop them for missing pricing rows. The backend
 * returns missing-pricing carriers with shipping_fee=null +
 * needs_pricing_quote=true so the UI renders a "Price on request" affordance
 * instead of silently honouring the pricing gap and dropping the carrier.
 */
export const buildAvailableCarriersRequest = (
  orderData: Order,
  sourceRegionUid: string,
  restrictedCarrierUids?: string[],
): AvailableCarriersRequest => {
  const productsValue = (orderData.products || []).reduce((sum, product) => {
    const price = parseFloat(String(product.price)) || 0;
    const quantity = Number(product.quantity) || 1;
    return sum + price * quantity;
  }, 0);

  const optionsArrayFromOptions: string[] = (orderData.options || []).map((opt) =>
    typeof opt === 'string' ? opt : opt.type,
  );
  const optionsArray: string[] =
    optionsArrayFromOptions.length > 0
      ? optionsArrayFromOptions
      : [
          ...(orderData.id_check ? ['id_check'] : []),
          ...(orderData.clue_verification ? ['clue_link_verification'] : []),
        ];

  return {
    /*
     * BOXY-301 §5.3 — spread-or-nothing, matching what the CREATE payload
     * does in useUpdateOrderPricePreview: a single-box order must send no
     * `boxes` key at all, because the backend refuses a one-element list.
     *
     * Without this the picker was asked about `size` only — the LARGEST box —
     * so a rate-shopper merchant was shown carriers that creation would then
     * refuse for one of the smaller boxes. The backend has accepted `boxes`
     * on this endpoint since §5.3; nothing was sending it. Fixed in the
     * BUILDER rather than at the call sites: all five (add-new-order twice,
     * edit-order, order-summary, select-carrier) go through here.
     */
    ...(Array.isArray(orderData.boxes) && orderData.boxes.length >= 2
      ? { boxes: orderData.boxes.map((box) => ({ size: box.size })) }
      : {}),
    // BOXY-501 — the service picked on the form; the picker prices THAT one.
    ...(orderData.service_code ? { service_code: orderData.service_code } : {}),
    source_region_uid: sourceRegionUid,
    destination_region_uid: orderData.contact?.region_uid || '',
    type: orderData.type || 'DEFAULT',
    size: orderData.size || 'S',
    is_fragile: orderData.is_fragile || false,
    // TEMP-5: backend filters the carrier list down to temperature-capable
    // carriers for the destination province when true.
    temperature_control: orderData.temperature_control || false,
    products_value: productsValue,
    options: optionsArray,
    ...(restrictedCarrierUids && restrictedCarrierUids.length > 0
      ? { restricted_carrier_uids: restrictedCarrierUids }
      : {}),
    // BOXY-311 — so each row's large-order fee is priced on the same cash
    // basis the confirmation step and the charge use.
    ...(orderData.shipment_fee_type ? { shipment_fee_type: orderData.shipment_fee_type } : {}),
    ...(orderData.fee_customer_payable
      ? { fee_customer_payable: Number(orderData.fee_customer_payable) }
      : {}),
    ...(orderData.payment_type ? { payment_type: orderData.payment_type } : {}),
  };
};

/**
 * Processes carrier preview response and tags carriers with service type
 */
export const processCarrierPreviewResponse = (response: {
  object: {
    same_day_carriers?: CarrierPreviewItem[];
    next_day_carriers?: CarrierPreviewItem[];
  };
}): CarrierPreviewItem[] => {
  // Tag same-day carriers with serviceType
  const sameDayCarriers = (response.object.same_day_carriers || []).map((carrier) => ({
    ...carrier,
    serviceType: ServiceTypeEnum.SAME_DAY,
  }));

  // Tag next-day carriers with serviceType
  const nextDayCarriers = (response.object.next_day_carriers || []).map((carrier) => ({
    ...carrier,
    serviceType: ServiceTypeEnum.NEXT_DAY,
  }));

  // Combine both arrays
  return [...sameDayCarriers, ...nextDayCarriers];
};
